import { apiClient } from '@/api/client';
import { z } from 'zod';
import { Friend, FriendRequest, FriendshipStatusResponse, SuggestedFriend } from '@/types/friendship';
import {
    mapFriendRequestWireToDomain,
    mapFriendshipStatusWireToDomain,
    mapFriendshipWireToDomain,
    mapSuggestedFriendWireToDomain,
} from '../mappers/friendship';
import {
    friendRequestWireSchema,
    friendshipWireSchema,
    friendshipStatusWireSchema,
    paginatedFriendRequestsWireSchema,
    paginatedFriendshipsWireSchema,
    suggestedFriendWireSchema,
    type FriendRequestWire,
    type FriendshipWire,
    type FriendshipStatusWire,
    type PaginatedFriendRequestsWire,
    type PaginatedFriendshipsWire,
    type SuggestedFriendWire,
} from '../schemas/friendship';

/**
 * Domain-oriented API module for friendship operations
 */

/**
 * Send a friend request
 * POST /api/friends/request
 */
export interface SendFriendRequestRequest {
  receiver_id: string;
}

export async function sendFriendRequest(
  receiverId: string
): Promise<FriendRequest> {
  const endpoint = '/api/friends/request';
  const wireData = await apiClient.post<unknown>(endpoint, {
    receiver_id: receiverId,
  });

  // Gateway returns { message: string, request: FriendRequestWire }
  const wireCandidate: unknown =
    wireData && typeof wireData === 'object' && 'request' in (wireData as any)
      ? (wireData as any).request
      : wireData;

  // Validate response against wire schema
  let validatedWire: FriendRequestWire;
  try {
    validatedWire = friendRequestWireSchema.parse(wireCandidate);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Friend request validation failed:', error.issues);
      const parseResult = friendRequestWireSchema.safeParse(wireCandidate);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid friend request data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return mapFriendRequestWireToDomain(validatedWire);
}

/**
 * Get incoming pending friend requests
 * GET /api/friends/requests
 */
export interface ListFriendRequestsParams {
  page?: number;
  per_page?: number;
}

export async function getFriendRequests(
  params: ListFriendRequestsParams = {}
): Promise<FriendRequest[]> {
  const queryParams: string[] = [];
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/friends/requests${queryString}`;

  // Gateway returns { requests: [...] } not paginated format
  const wireData = await apiClient.get<{ requests: FriendRequestWire[] }>(endpoint);

  // Validate response
  let validatedWire: { requests: FriendRequestWire[] };
  try {
    validatedWire = z.object({
      requests: z.array(friendRequestWireSchema),
    }).parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Friend requests validation failed:', error.issues);
      const parseResult = z.object({
        requests: z.array(friendRequestWireSchema),
      }).safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid friend requests data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return validatedWire.requests.map(mapFriendRequestWireToDomain);
}

/**
 * Get sent pending friend requests
 * GET /api/friends/requests/outgoing
 */
export async function getSentRequests(
  params: ListFriendRequestsParams = {}
): Promise<FriendRequest[]> {
  const queryParams: string[] = [];
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/friends/requests/outgoing${queryString}`;

  const wireData = await apiClient.get<PaginatedFriendRequestsWire>(endpoint);

  // Validate response
  let validatedWire: PaginatedFriendRequestsWire;
  try {
    validatedWire = paginatedFriendRequestsWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Sent friend requests validation failed:', error.issues);
      const parseResult = paginatedFriendRequestsWireSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid sent friend requests data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return validatedWire.items.map(mapFriendRequestWireToDomain);
}

/**
 * Accept a friend request
 * POST /api/friends/request/{id}/accept
 */
export async function acceptFriendRequest(requestId: string): Promise<void> {
  const endpoint = `/api/friends/request/${requestId}/accept`;
  await apiClient.post(endpoint, {});
}

/**
 * Reject a friend request
 * POST /api/friends/request/{id}/reject
 */
export async function rejectFriendRequest(requestId: string): Promise<void> {
  const endpoint = `/api/friends/request/${requestId}/reject`;
  await apiClient.post(endpoint, {});
}

/**
 * Cancel a sent friend request
 * DELETE /api/friends/request/{id}
 */
export async function cancelFriendRequest(requestId: string): Promise<void> {
  const endpoint = `/api/friends/request/${requestId}`;
  await apiClient.delete(endpoint);
}

/**
 * Get all friends
 * GET /api/friends
 */
export interface ListFriendsParams {
  page?: number;
  per_page?: number;
}

export async function getFriends(
  params: ListFriendsParams = {}
): Promise<Friend[]> {
  const queryParams: string[] = [];
  if (params.page !== undefined) {
    queryParams.push(`page=${params.page}`);
  }
  if (params.per_page !== undefined) {
    queryParams.push(`per_page=${params.per_page}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/friends${queryString}`;

  const wireData = await apiClient.get<PaginatedFriendshipsWire>(endpoint);

  // Validate response
  let validatedWire: PaginatedFriendshipsWire;
  try {
    validatedWire = paginatedFriendshipsWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Friends validation failed:', error.issues);
      const parseResult = paginatedFriendshipsWireSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid friends data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return validatedWire.items.map(mapFriendshipWireToDomain);
}

/**
 * Get all friends for a specific user
 * GET /api/friends/user/{userId}
 */
export async function getUserFriends(userId: string): Promise<Friend[]> {
  const endpoint = `/api/friends/user/${userId}`;

  const wireData = await apiClient.get<{ friends: FriendshipWire[] }>(endpoint);

  // Validate response
  let validatedWire: { friends: FriendshipWire[] };
  try {
    validatedWire = z.object({
      friends: z.array(friendshipWireSchema),
    }).parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] User friends validation failed:', error.issues);
      const parseResult = z.object({
        friends: z.array(friendshipWireSchema),
      }).safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid user friends data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return validatedWire.friends.map(mapFriendshipWireToDomain);
}

/**
 * Remove a friend
 * DELETE /api/friends/{user_id}
 */
export async function removeFriend(userId: string): Promise<void> {
  const endpoint = `/api/friends/${userId}`;
  await apiClient.delete(endpoint);
}

/**
 * Get suggested friends
 * GET /api/friends/suggested
 */
export async function getSuggestedFriends(
  params: { limit?: number } = {}
): Promise<SuggestedFriend[]> {
  const queryParams: string[] = [];
  if (params.limit !== undefined) {
    queryParams.push(`limit=${params.limit}`);
  }

  const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  const endpoint = `/api/friends/suggested${queryString}`;

  const wireData = await apiClient.get<{ suggestions: SuggestedFriendWire[] }>(endpoint);

  const responseSchema = z.object({
    suggestions: z.array(suggestedFriendWireSchema),
  });

  let validatedWire: { suggestions: SuggestedFriendWire[] };
  try {
    validatedWire = responseSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Suggested friends validation failed:', error.issues);
      const parseResult = responseSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid suggested friends data received from server');
      }
    } else {
      throw error;
    }
  }

  return validatedWire.suggestions.map(mapSuggestedFriendWireToDomain);
}

/**
 * Check friendship status with a user
 * GET /api/friends/status/{user_id}
 */
export async function getFriendshipStatus(
  userId: string,
  signal?: AbortSignal
): Promise<FriendshipStatusResponse> {
  const endpoint = `/api/friends/status/${userId}`;
  const wireData = await apiClient.get<FriendshipStatusWire>(endpoint, true, signal);

  // Validate response
  let validatedWire: FriendshipStatusWire;
  try {
    validatedWire = friendshipStatusWireSchema.parse(wireData);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error('[API] Friendship status validation failed:', error.issues);
      const parseResult = friendshipStatusWireSchema.safeParse(wireData);
      if (parseResult.success) {
        validatedWire = parseResult.data;
      } else {
        throw new Error('Invalid friendship status data received from server');
      }
    } else {
      throw error;
    }
  }

  // Map to domain format
  return mapFriendshipStatusWireToDomain(validatedWire);
}
